import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PhoneIcon } from '@rsos/assets/icons';
import { Button } from '@rsos/components/capstone/base';
import {
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_ENDED,
  RCS_CONVERSATION_STATUS_PENDING,
} from '@rsos/constants/rcsStatuses';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { RootState } from '../../../../app';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import { CollapseButton } from '../SharedChatUI/CollapseButton';
import { TranslationMenu } from '../SharedChatUI/TranslationMenu';
import { TranslationMenuButton } from '../SharedChatUI/TranslationMenuButton';
import RcsAcceptDeclineChat from './RcsAcceptDeclineChat';
import styles from './RcsChat.module.scss';
import RcsEndChat from './RcsEndChat';
import { CHAT_HEADER_END, CHAT_HEADER_ACCEPT_DECLINE } from './constants';

interface RcsChatHeaderProps {
  showButtons: string | null;
}

const RcsChatHeader = ({ showButtons }: RcsChatHeaderProps) => {
  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  const displayPhoneNumber = formatInternationalPhoneNumberHuman(
    selectedCallerID || '',
  );

  const isFetchingMetadata = useMemo(
    () => rcsConversation?.loading?.getRcsConversationMetadata,
    [rcsConversation],
  );

  const headerText = useMemo(() => {
    const text: Record<number, JSX.Element> = {
      [RCS_CONVERSATION_STATUS_PENDING]: (
        <>
          <span className={styles.redCircle} /> Google RCS
        </>
      ),
      [RCS_CONVERSATION_STATUS_ACTIVE]: (
        <>
          <span className={styles.redCircle} /> Google RCS
        </>
      ),
      [RCS_CONVERSATION_STATUS_ENDED]: <>Closed Google RCS</>,
    };
    if (!rcsConversation || !rcsConversation.data) return '';
    return text[rcsConversation.data.conversation_status] || null;
  }, [rcsConversation]);

  const renderHeaderContent = () => {
    if (isFetchingMetadata) {
      return (
        <div className={styles.loadingWrapper}>
          <div className={styles.loadingInnerBox}>
            <Button
              btnType="tertiary"
              disabled={isFetchingMetadata}
              isLoadingProp={isFetchingMetadata}
              noMargin
              size="med"
              grow
            />
          </div>
        </div>
      );
    }

    const buttonComponents: Record<string, JSX.Element> = {
      [CHAT_HEADER_END]: (
        <div className={styles.buttonRow}>
          <RcsEndChat />
        </div>
      ),
      [CHAT_HEADER_ACCEPT_DECLINE]: (
        <div className={styles.buttonRow}>
          <RcsAcceptDeclineChat />
        </div>
      ),
    };

    return showButtons ? (buttonComponents[showButtons] ?? null) : null;
  };

  return (
    <div
      className={styles.headerContainer}
      data-name="text-from-911-chat-header"
    >
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <span className={styles.headerStatus}>{headerText}</span>
          <div className={styles.phoneRow}>
            {!hasAccessToUniteEventPane && (
              <span className={styles.phoneIcon}>
                <PhoneIcon
                  width={10}
                  height={12}
                  viewboxWidth={11}
                  viewboxHeight={15}
                />
              </span>
            )}
            <span
              className={`${styles.phoneNumber} ${hasAccessToUniteEventPane ? styles.compact : ''}`}
            >
              {displayPhoneNumber}
            </span>
          </div>
        </div>
        <div className={styles.iconButtonsBox}>
          <TranslationMenuButton />
          <CollapseButton />
        </div>
      </div>
      <TranslationMenu />
      {renderHeaderContent()}
    </div>
  );
};

export default RcsChatHeader;
